import type { Point } from "./pinch-geometry.ts";

export type PinchSession = { fromFit: boolean; fromLift: boolean };

export type TouchPoint = {
    identifier: number;
    clientX: number;
    clientY: number;
    target: EventTarget | null;
};

export type TouchLike = {
    touches: ArrayLike<TouchPoint>;
    changedTouches: ArrayLike<TouchPoint>;
    preventDefault(): void;
};

export type TouchSequence = {
    fingers: Map<number, Point>;
    startAt: number;
    travel: number;
    pinch: PinchSession | null;
};

const pointOf = (t: TouchPoint): Point => ({ x: t.clientX, y: t.clientY });

export function startSequence(): TouchSequence {
    return {
        fingers: new Map(),
        startAt: performance.now(),
        travel: 0,
        pinch: null,
    };
}

export function addFingers(seq: TouchSequence, list: readonly TouchPoint[]) {
    for (const t of list) seq.fingers.set(t.identifier, pointOf(t));
}

export function dropLiftedFingers(
    seq: TouchSequence,
    touches: ArrayLike<TouchPoint>,
) {
    const live = new Map(
        Array.from(touches, (t) => [t.identifier, pointOf(t)] as const),
    );
    const before = seq.fingers.size;
    for (const id of seq.fingers.keys()) {
        const p = live.get(id);
        if (p) seq.fingers.set(id, p);
        else seq.fingers.delete(id);
    }
    return seq.fingers.size !== before;
}

export function holdsAny(
    seq: TouchSequence | null,
    list: ArrayLike<TouchPoint>,
) {
    return Array.from(list).some((t) => seq?.fingers.has(t.identifier));
}

export function leadPoints(seq: TouchSequence | null) {
    return seq ? [...seq.fingers.values()].slice(0, 2) : [];
}

export function createVelocitySampler() {
    let samples: { t: number; x: number; y: number }[] = [];
    return {
        reset() {
            samples = [];
        },
        sample(p: Point) {
            const t = performance.now();
            samples.push({ t, x: p.x, y: p.y });
            samples = samples.filter((entry) => t - entry.t < 90);
        },
        velocity(): Point {
            const first = samples[0];
            const last = samples[samples.length - 1];
            if (!first || !last || last === first) return { x: 0, y: 0 };
            if (performance.now() - last.t > 60) return { x: 0, y: 0 };
            const dt = last.t - first.t || 1;
            return {
                x: ((last.x - first.x) / dt) * 1000,
                y: ((last.y - first.y) / dt) * 1000,
            };
        },
    };
}

type TouchRouterHooks = {
    claims: () => boolean;
    ignores: (target: EventTarget | null) => boolean;
    anchor: (points: Point[]) => Point | null;
    begin: (seq: TouchSequence) => void;
    move: (seq: TouchSequence) => void;
    end: (seq: TouchSequence, lastPoint: Point | null) => void;
};

export function createTouchRouter(hooks: TouchRouterHooks) {
    let sequence: TouchSequence | null = null;

    const points = () => leadPoints(sequence);

    const finish = (lastPoint: Point | null) => {
        const seq = sequence;
        sequence = null;
        if (seq) hooks.end(seq, lastPoint);
    };

    const abandon = () => {
        if (sequence) finish(hooks.anchor(points()));
    };

    const reconcile = (e: TouchLike) => {
        if (!sequence) return;
        const anchor = hooks.anchor(points());
        if (!dropLiftedFingers(sequence, e.touches)) return;
        if (sequence.fingers.size === 0) finish(anchor);
        else hooks.begin(sequence);
    };

    const track = (list: readonly TouchPoint[]) => {
        sequence ??= startSequence();
        addFingers(sequence, list);
        hooks.begin(sequence);
    };

    const onStart = (e: TouchLike) => {
        if (!hooks.claims()) return;
        reconcile(e);
        if (!hooks.claims()) return;
        const fresh = Array.from(e.changedTouches).filter(
            (t) => !hooks.ignores(t.target),
        );
        if (fresh.length === 0) return;
        e.preventDefault();
        track(fresh);
    };

    const onMove = (e: TouchLike) => {
        if (!hooks.claims()) return;
        e.preventDefault();
        reconcile(e);
        if (sequence) hooks.move(sequence);
    };

    const onEnd = (e: TouchLike) => {
        if (!hooks.claims()) return;
        if (holdsAny(sequence, e.changedTouches)) e.preventDefault();
        reconcile(e);
    };

    const onCancel = (e: TouchLike) => {
        if (!hooks.claims()) return;
        if (holdsAny(sequence, e.changedTouches)) abandon();
        else reconcile(e);
    };

    const listen = (signal: AbortSignal) => {
        const capture = { passive: false, capture: true, signal };
        window.addEventListener("touchstart", onStart, capture);
        window.addEventListener("touchmove", onMove, capture);
        window.addEventListener("touchend", onEnd, capture);
        window.addEventListener("touchcancel", onCancel, capture);
    };

    return {
        points,
        track,
        abandon,
        listen,
        reset: () => {
            sequence = null;
        },
    };
}
